import React, { useRef, useState } from "react";
import { Box, Fade, Popper, Typography } from "@mui/material";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { NavIcon } from "./navIcons";
import { NavGroup } from "./navModel";
import { SectionLabel, isPlainClick } from "./navPrimitives";
import { NAV, ghostButton, overlayPaper, rowSx } from "./navTheme";
import { MOD_K } from "../../../utils/platform";

interface SidebarRailProps {
  groups: NavGroup[];
  activeLink: string;
  onExpand: () => void;
  onOpenPalette: () => void;
}

/** Collapsed nav: group icons that pop their pages out on hover or focus. */
const SidebarRail: React.FC<SidebarRailProps> = ({
  groups,
  activeLink,
  onExpand,
  onOpenPalette,
}) => {
  const navigate = useNavigate();
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openFlyout = (groupId: string, element: HTMLElement) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenGroupId(groupId);
    setAnchorEl(element);
  };

  // Grace period so the pointer can cross the gap into the flyout.
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpenGroupId(null);
      setAnchorEl(null);
    }, 140);
  };

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };

  const openGroup = groups.find((group) => group.id === openGroupId) ?? null;
  const railButton = ghostButton(44, 13);

  return (
    <>
      <Box
        component="nav"
        aria-label="Main navigation"
        sx={{
          width: NAV.widthRail,
          flexShrink: 0,
          height: "100%",
          backgroundColor: NAV.surface,
          borderRight: `1px solid ${NAV.border}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
          py: 1.5,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onExpand}
          aria-label="Expand navigation"
          sx={{ ...railButton, mb: 0.5 }}
        >
          <Box
            component="img"
            src="/SPARK LOGO-white.png"
            alt="Spark"
            sx={{ width: 26, height: 26, objectFit: "contain" }}
          />
        </Box>

        <Box
          component="button"
          type="button"
          onClick={onOpenPalette}
          aria-label="Search pages"
          title={`Search pages (${MOD_K})`}
          sx={railButton}
        >
          <SearchRounded sx={{ fontSize: 20 }} />
        </Box>

        <Box
          sx={{ width: 28, height: "1px", backgroundColor: NAV.border, my: 1 }}
        />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.5,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {groups.map((group) => {
            const containsActive = group.pages.some(
              (page) => page.link === activeLink,
            );
            const isOpen = openGroupId === group.id;
            return (
              <Box
                key={group.id}
                component="button"
                type="button"
                aria-label={group.label}
                aria-expanded={isOpen}
                onMouseEnter={(event: React.MouseEvent<HTMLElement>) =>
                  openFlyout(group.id, event.currentTarget)
                }
                onMouseLeave={scheduleClose}
                onFocus={(event: React.FocusEvent<HTMLElement>) =>
                  openFlyout(group.id, event.currentTarget)
                }
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  openFlyout(group.id, event.currentTarget)
                }
                sx={{
                  ...railButton,
                  position: "relative",
                  color: containsActive ? NAV.accent : NAV.textIdle,
                  backgroundColor:
                    containsActive || isOpen ? NAV.accentSoft : "transparent",
                  border: `1px solid ${containsActive ? NAV.accentBorder : "transparent"}`,
                }}
              >
                <NavIcon
                  id={group.id}
                  variant="group"
                  size={20}
                  color="inherit"
                />
                {containsActive && (
                  <Box
                    component={motion.span}
                    layoutId="nav-rail-marker"
                    transition={NAV.spring}
                    sx={{
                      position: "absolute",
                      left: -10,
                      top: "50%",
                      translate: "0 -50%",
                      width: 3,
                      height: 20,
                      borderRadius: "0 3px 3px 0",
                      backgroundColor: NAV.accent,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Popper
        open={Boolean(openGroup && anchorEl)}
        anchorEl={anchorEl}
        placement="right-start"
        transition
        sx={{ zIndex: 1400 }}
        modifiers={[{ name: "offset", options: { offset: [0, 10] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={140}>
            <Box
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              sx={{
                ...overlayPaper(14),
                minWidth: 226,
                maxHeight: "70vh",
                overflowY: "auto",
                p: 0.75,
              }}
            >
              <SectionLabel sx={{ px: 1.25, pt: 0.75, pb: 1 }}>
                {openGroup?.label}
              </SectionLabel>
              {openGroup?.pages.map((page) => {
                const isActive = page.link === activeLink;
                return (
                  <Box
                    key={page.id}
                    component="a"
                    href={page.link}
                    onClick={(event: React.MouseEvent) => {
                      if (!isPlainClick(event)) return;
                      event.preventDefault();
                      navigate(page.link);
                      setOpenGroupId(null);
                      setAnchorEl(null);
                    }}
                    aria-current={isActive ? "page" : undefined}
                    sx={{
                      ...rowSx({ height: 40, radius: 10 }),
                      color: isActive ? NAV.accent : NAV.textIdle,
                      backgroundColor: isActive
                        ? NAV.accentSoft
                        : "transparent",
                      "@media (hover: hover)": {
                        "&:hover": {
                          backgroundColor: isActive ? NAV.accentSoft : NAV.hover,
                          color: isActive ? NAV.accent : NAV.textActive,
                        },
                      },
                    }}
                  >
                    <NavIcon
                      id={page.id}
                      groupId={openGroup.id}
                      size={17}
                      color="inherit"
                    />
                    <Typography
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: isActive ? 600 : 450,
                        color: "inherit",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {page.label}
                    </Typography>
                    {isActive && (
                      <ChevronRightRounded
                        sx={{ fontSize: 16, ml: "auto", color: NAV.accent }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default SidebarRail;
